import PropTypes from 'prop-types';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  Divider,
  Typography,
  Avatar,
  Button,
  Tooltip,
  IconButton,
  Stack
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { clearAdminSessionExceptRemember } from 'src/utils/clutchAdminAuth';
import AdminProfileDialog from './adminDialogs/AdminProfileDialog';
import AdminPasswordDialog from './adminDialogs/AdminPasswordDialog';
import AdminEmailChangeDialog from './adminDialogs/AdminEmailChangeDialog';

const SectionButton = styled(Button)(({ theme }) => ({
  justifyContent: 'flex-start',
  textTransform: 'none',
  color: theme.palette.common.white,
  fontSize: 13,
  fontWeight: 500,
  py: 1,
  px: 1.5,
  minHeight: 40,
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.08)
  }
}));

function profileImageUrl(user) {
  return user?.profileImage || user?.staff_member?.avatar || '';
}

export default function SidebarAdminPanel({ user, mini, onUserUpdated }) {
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const logout = () => {
    clearAdminSessionExceptRemember();
    navigate('/login', { replace: true });
  };

  const openProfile = () => setProfileOpen(true);

  const avatar = (
    <Avatar
      src={profileImageUrl(user)}
      alt={user?.name || 'Admin'}
      sx={{
        width: mini ? 40 : 44,
        height: mini ? 40 : 44,
        mx: mini ? 'auto' : 0
      }}
    />
  );

  const row = (icon, label, onClick) => {
    if (mini) {
      return (
        <Tooltip key={label} title={label} placement="right" arrow>
          <IconButton onClick={onClick} sx={{ color: 'common.white', mx: 'auto' }}>
            <Iconify icon={icon} width={22} />
          </IconButton>
        </Tooltip>
      );
    }
    return (
      <SectionButton key={label} fullWidth startIcon={<Iconify icon={icon} width={20} />} onClick={onClick}>
        {label}
      </SectionButton>
    );
  };

  return (
    <>
      <Box sx={{ px: mini ? 0.5 : 1.25, py: 1.5, pb: 2 }}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.25)', mb: 1.25, display: mini ? 'none' : 'block' }} />
        {!mini && (
          <Typography
            variant="overline"
            sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 10, letterSpacing: 1.2, px: 0.5, display: 'block', mb: 1 }}
          >
            Local admin area
          </Typography>
        )}

        <Stack spacing={mini ? 1 : 0.5} alignItems={mini ? 'center' : 'stretch'}>
          {mini ? (
            <Tooltip title="Edit name & photo" placement="right" arrow>
              <Box
                onClick={openProfile}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'center',
                  borderRadius: 1,
                  py: 0.5,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
                }}
              >
                {avatar}
              </Box>
            </Tooltip>
          ) : (
            <Box
              onClick={openProfile}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openProfile();
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                mb: 0.5,
                px: 0.75,
                py: 0.75,
                borderRadius: 1,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
              }}
            >
              {avatar}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap variant="subtitle2" sx={{ color: 'common.white', fontSize: 13 }}>
                  {user?.name || 'Admin'}
                </Typography>
                <Typography noWrap variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  {user?.email}
                </Typography>
              </Box>
            </Box>
          )}

          {row('mdi:email-edit-outline', 'Change email', () => setEmailOpen(true))}
          {row('mdi:lock-reset', 'Change password', () => setPasswordOpen(true))}
          {row('solar:logout-broken', 'Log out', logout)}
        </Stack>
      </Box>

      <AdminProfileDialog
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        user={user}
        onUpdated={onUserUpdated}
      />
      <AdminEmailChangeDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        user={user}
        onUpdated={onUserUpdated}
      />
      <AdminPasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </>
  );
}

SidebarAdminPanel.propTypes = {
  user: PropTypes.object,
  mini: PropTypes.bool,
  onUserUpdated: PropTypes.func
};
